// useSelfLearnerProfile.js
import { useState, useEffect, useContext } from "react";
import { message, Upload } from "antd";
import { ProfileContext } from "Common/hooks/context/ProfileContext";
import { getCareerLevelList } from "Common/Constants/Enums/Individual/CareerLevelEnum";
import { getEducationLevelList } from "Common/Constants/Enums/Individual/EducationLevelEnum";
import { getProfessionTypeList } from "Common/Constants/Enums/Individual/ProfessionTypeEnum";
import { fetchExpertProfile, updateExpertProfile } from "../Services/ExpertProfileService";

const useExpertProfile = (form) => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [fileList, setFileList] = useState([]);

  const token = localStorage.getItem("token");
  const { markProfileComplete, fetchProfileStatus } = useContext(ProfileContext);

  // -------------------- FETCH PROFILE --------------------
  const getExpertProfile = async () => {
    try {
      setLoading(true);
      const response = await fetchExpertProfile(token);
      const data = response.data;

      setProfileData(data);

      // ---- Set Form Fields using API Response ----
      form.setFieldsValue({
        firstName: data.firstName,
        lastName: data.lastName,
        phoneNo: data.phoneNo,
        country: data.country,
        state: data.state,
        city: data.city,
        yearsOfExperience:data.yearsOfExperience
      });

      if (data.profileImage) {
        setImageUrl(`${process.env.REACT_APP_URL}${data.profileImage}`);
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      message.error("Failed to load profile data");
    } finally {
      setLoading(false);
    }
  };

  // -------------------- UPDATE PROFILE --------------------
  const updateProfile = async (values, navigate) => {
    try {
      setUpdating(true);

      // Prepare data according to API requirements
      const updateData = {
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNo: values.phoneNo,
        country: values.country,
        state: values.state,
        city: values.city,
        yearsOfExperience:values.yearsOfExperience
      };

      console.log("Update data:", updateData);

      const response = await updateExpertProfile(token, updateData, fileList);
      
      setProfileData(response.data);
      message.success("Profile updated successfully!");

      markProfileComplete();
      
      // Give a moment for the context to update
      setTimeout(() => {
        fetchProfileStatus();
      }, 500);

      if (response.data.profileImage) {
        setImageUrl(`${process.env.REACT_APP_URL}${response.data.profileImage}`);
      }

      setFileList([]);
      
      return true;
    } catch (error) {
      console.error("Error updating profile:", error);
      message.error("Failed to update profile");
      return false;
    } finally {
      setUpdating(false);
    }
  };

  // -------------------- IMAGE HANDLERS --------------------
  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    const isLt2M = file.size / 1024 / 1024 < 2;

    if (!isJpgOrPng) {
      message.error("You can only upload JPG/PNG file!");
      return Upload.LIST_IGNORE;
    }

    if (!isLt2M) {
      message.error("Image must be smaller than 2MB!");
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  const handleImageChange = (info) => {
    let newFileList = [...info.fileList].slice(-1);

    newFileList = newFileList.map((file) => {
      if (file.response) file.url = file.response.url;
      return file;
    });

    setFileList(newFileList);

    if (newFileList.length > 0 && newFileList[0].originFileObj) {
      const reader = new FileReader();
      reader.onload = (e) => setImageUrl(e.target.result);
      reader.readAsDataURL(newFileList[0].originFileObj);
    }
  };

  useEffect(() => {
    getExpertProfile();
  }, []);

  return {
    profileData,
    loading,
    updating,
    imageUrl,
    fileList,
    beforeUpload,
    handleImageChange,
    updateProfile,
    setFileList,
  };
};

export default useExpertProfile;